/**
 * Lazily creates (and caches) object URLs for image-file thumbnails, keyed by File reference.
 *
 * Object URLs created via URL.createObjectURL never get garbage collected on their own - callers
 * must revoke them once the underlying File is no longer selected/displayed, and again when the
 * owning component is destroyed. This class centralizes that create/revoke lifecycle so it only
 * has to be gotten right in one place.
 *
 * Not a service - each consuming component should own its own instance, e.g.
 * `private readonly thumbnailCache = new ImageThumbnailCache();`.
 */
export class ImageThumbnailCache {
  private readonly urls = new Map<File, string>();

  /** Returns a cached object URL for image files; null for non-image files. */
  public getUrl(file: File): string | null {
    if (!file.type.startsWith('image/')) {
      return null;
    }
    let url = this.urls.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      this.urls.set(file, url);
    }
    return url;
  }

  /** Revokes cached URLs for any file no longer present in filesToKeep. */
  public revokeExcept(filesToKeep: File[]): void {
    const keepSet = new Set(filesToKeep);
    for (const [file, url] of this.urls) {
      if (!keepSet.has(file)) {
        URL.revokeObjectURL(url);
        this.urls.delete(file);
      }
    }
  }

  /** Revokes all cached URLs. Call from ngOnDestroy. */
  public clear(): void {
    this.urls.forEach(url => URL.revokeObjectURL(url));
    this.urls.clear();
  }
}
